/**
 * Platform-wide user administration.
 *
 * Who exists, what they can reach, and — new here — changing it without leaving
 * the list. Search and filtering run on the server so the browser never receives
 * rows it then hides: a client-side filter over a full download is a data leak
 * wearing a convenience costume.
 *
 * The shape follows how account lists work where this problem is already solved
 * (Okta, Google Workspace Admin, Auth0, Stripe's team screen): a faceted toolbar
 * whose chips carry the count of what is behind them, one badge per person saying
 * what they actually are, and a row menu whose actions open dialogs over the
 * list rather than navigating away. Editing four people should not cost four page
 * loads and four trips back.
 *
 * The detail page stays for what a dialog cannot hold: every grant, live session
 * count, and the account's own history. This screen is for the change; that one
 * is for the picture.
 */
import { ROLE_LABEL } from '../roles.ts';
import { useState } from 'react';
import { useLoaderData, useSearchParams } from 'react-router';
import type { AdminClinicList, AdminStore, AdminUser, AdminUserList, ListResponse } from '../api.ts';
import {
  Card,
  Empty,
  Pill,
  RowLink,
  StatusPill,
  Table,
  jaDate,
  jaNumber,
} from '../components/ui.tsx';
import { Select } from '../components/Select.tsx';
import { imageUrl } from '../api.ts';

/** Rows per request, first page included. */
const PAGE = 10;
/*
 * The tables behind this list. A module constant, not an inline array: a new
 * array on every render would look to `usePaged` like a different list and
 * reset the pages it had already loaded.
 */
const USER_TABLES = ['users', 'clinic_memberships', 'store_memberships'] as const;
import { CreateUserDialog } from '../components/UserDialogs.tsx';
import { usePaged } from '../paging.ts';

interface LoaderData {
  users: AdminUserList;
  clinics: AdminClinicList;
  stores: ListResponse<AdminStore>;
}


const STATUS_LABEL: Record<string, string> = {
  ACTIVE: '有効',
  SUSPENDED: '停止中',
  ARCHIVED: 'アーカイブ',
};

/**
 * What this person is, in one badge.
 *
 * The bucket comes from the server (see ROLE_BUCKET in admin/routes.ts) rather
 * than being re-derived from the counts beside it: the filter and the chip counts
 * use that expression, and a badge computed differently is a badge that
 * eventually disagrees with the filter that found the row.
 */
function RoleCell({ user }: { user: AdminUser }) {
  if (user.role_bucket === 'JMC_ADMIN') return <Pill tone="danger">JMC管理者</Pill>;
  if (user.role_bucket === 'CLINIC_ADMIN') {
    return <Pill>クリニック管理（{jaNumber(user.clinic_admin_count)}）</Pill>;
  }
  if (user.role_bucket === 'STORE_ADMIN') {
    return <Pill>{ROLE_LABEL.STORE_ADMIN}（{jaNumber(user.store_count)}）</Pill>;
  }
  if (user.role_bucket === 'STORE_USER') {
    return <Pill tone="muted">{ROLE_LABEL.STORE_USER}（{jaNumber(user.store_count)}）</Pill>;
  }
  return <Pill tone="warn">所属なし</Pill>;
}

export default function AdminUsers() {
  const { users, clinics, stores } = useLoaderData<LoaderData>();

  /*
   * Ten rows, then more as the reader reaches them. The query string travels
   * with the request: page two of a filtered list has to carry the same filter,
   * or the rows that arrive are not the rows being looked at.
   */
  const [listParams] = useSearchParams();
  const filters = new URLSearchParams();
  for (const key of ['q', 'status', 'role', 'clinicId'] as const) {
    const value = listParams.get(key);
    if (value) filters.set(key, value);
  }
  const paged = usePaged(
    `/admin/users?${filters.toString()}`,
    users.items,
    PAGE,
    true,
    USER_TABLES,
  );
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') ?? '');

  /**
   * Which dialog is open, and for whom.
   *
   * One piece of state rather than three booleans plus a selected user: two
   * dialogs open at once is not a state this screen has, and modelling it as one
   * makes that unrepresentable instead of merely unlikely.
   */
  const [dialog, setDialog] = useState<
    { kind: 'edit' | 'role' | 'membership'; user: AdminUser } | { kind: 'create' } | null
  >(null);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const status = params.get('status') ?? '';
  const role = params.get('role') ?? '';
  const clinicId = params.get('clinicId') ?? '';

  const facet = (kind: string, key: string) =>
    users.facets.find((row) => row.kind === kind && row.key === key)?.count ?? 0;
  const total = users.facets
    .filter((row) => row.kind === 'role')
    .reduce((sum, row) => sum + Number(row.count), 0);

  return (
    <Card
      title="ユーザー管理（全クリニック）"
      caption="行を選ぶと、そのユーザーの詳細と編集画面が開きます。記事本文の閲覧には別途サポート権限昇格が必要です。"
      actions={
        <button className="btn btn--sm" type="button" onClick={() => setDialog({ kind: 'create' })}>
          ＋ ユーザーを追加
        </button>
      }
    >
      <form
        className="tenantbar"
        onSubmit={(event) => {
          event.preventDefault();
          setParam('q', query.trim());
        }}
      >
        <div className="field" style={{ marginBottom: 0, minWidth: 220 }}>
          <label htmlFor="q">検索</label>
          <input
            id="q"
            value={query}
            placeholder="氏名またはメールアドレス"
            maxLength={120}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        {/*
          The clinic filter is a select rather than chips: there is one option per
          tenant, and a chip row that wraps to four lines is not a filter anybody
          reads. It matches either shape of grant — clinic-wide or store — because
          "who can reach this clinic" does not care which.
        */}
        <div className="field" style={{ marginBottom: 0, minWidth: 200 }}>
          <label htmlFor="clinic">クリニック</label>
          <Select
            id="clinic"
            value={clinicId}
            onChange={(value) => setParam('clinicId', value)}
            options={[
              { value: '', label: 'すべて' },
              ...clinics.items.map((clinic) => ({ value: clinic.id, label: clinic.name })),
            ]}
          />
        </div>

        <div className="field" style={{ marginBottom: 0, minWidth: 150 }}>
          <label htmlFor="status">状態</label>
          <Select
            id="status"
            value={status}
            onChange={(value) => setParam('status', value)}
            options={[
              { value: '', label: 'すべて' },
              ...(['ACTIVE', 'SUSPENDED', 'ARCHIVED'] as const).map((key) => ({
                value: key,
                label: `${STATUS_LABEL[key]}（${facet('status', key)}）`,
              })),
            ]}
          />
        </div>

        <button className="btn btn--sm" type="submit" style={{ alignSelf: 'end' }}>
          検索
        </button>
      </form>

      {/*
        Role as chips, with counts. 所属なし 4 is both the filter and the answer
        to "is anyone stranded without access", which a closed dropdown can never
        be. Counts are over every user, not the filtered page.
      */}
      <div className="chips tenantchips">
        <button
          type="button"
          className="chip"
          aria-pressed={role === ''}
          onClick={() => setParam('role', '')}
        >
          すべて（{total}）
        </button>
        {(['JMC_ADMIN', 'CLINIC_ADMIN', 'STORE_ADMIN', 'STORE_USER', 'NONE'] as const).map((key) => (
          <button
            key={key}
            type="button"
            className="chip"
            aria-pressed={role === key}
            onClick={() => setParam('role', key)}
          >
            {ROLE_LABEL[key]}（{facet('role', key)}）
          </button>
        ))}
      </div>

      {/* Keyed on the active filter: a new result set fades in over the old
          one's space, instead of the list emptying first. */}
      {/* Opacity only: a transform here would reflow the table's columns. */}
      <div key={`${params.get('q') ?? ''}-${status}-${role}-${clinicId}`} className="animate-fade-in">
        {users.items.length === 0 ? (
          <Empty>該当するユーザーがいません。</Empty>
        ) : (
          <Table
            caption={`${jaNumber(users.items.length)} 件を表示しています。`}
            head={
              <>
                <th scope="col">氏名</th>
                <th scope="col">メールアドレス</th>
                <th scope="col">権限</th>
                <th scope="col">所属クリニック</th>
                <th scope="col">状態</th>
                <th scope="col">最終ログイン</th>
              </>
            }
          >
            {paged.items.map((user) => (
              <tr key={user.id} className="row-link">
                <td>
                  {/*
                    The whole row opens the person.

                    It was a plain link on the name, because a stretched hit
                    area cannot coexist with a ⋯ menu in the last cell - the
                    overlay sits over the menu and swallows every click meant
                    for it. That menu is gone: its three editors were absorbed
                    into the person's own page, which left one item pointing
                    exactly where the name already went. With nothing left to
                    compete with, the row can be the target it looks like.
                  */}
                  {/*
                    The face beside the name, so a list of people reads as
                    people rather than as rows of text. The initial stands in
                    when there is no picture - never a silhouette, which is the
                    same for everybody and therefore says nothing.
                  */}
                  <RowLink to={`/admin/users/${user.id}`}>
                    <span className="rowname rowname--person">
                    <span className="rowavatar" aria-hidden>
                      {user.avatar_id ? (
                        <img src={imageUrl(user.avatar_id)} alt="" width={28} height={28} />
                      ) : (
                        (user.display_name || user.email || '?').trim().charAt(0).toUpperCase()
                      )}
                    </span>
                      <span>{user.display_name || '—'}</span>
                    </span>
                  </RowLink>
                </td>
                <td className="muted">{user.email ?? '—'}</td>
                <td>
                  <RoleCell user={user} />
                </td>
                <td>
                  {user.clinic_names ?? <span className="muted">—</span>}
                  {Number(user.store_count) > 0 && (
                    <span className="muted small" style={{ display: 'block' }}>
                      担当店舗 {jaNumber(user.store_count)} 件
                    </span>
                  )}
                </td>
                <td>
                  <StatusPill status={user.status} />
                </td>
                <td>{jaDate(user.last_login_at)}</td>
              </tr>
            ))}
          </Table>
        )}

        {/*
          The foot of the list: the observer's target, and the same action for
          somebody not scrolling with a mouse.
        */}
        {!paged.done && paged.items.length > 0 && (
          <div ref={paged.sentinel} className="more-rows">
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              onClick={paged.more}
              disabled={paged.loading}
            >
              {paged.loading
                ? '読み込み中…'
                : `さらに表示（残り${jaNumber(Math.max(0, (users.total ?? 0) - paged.items.length))}件）`}
            </button>
          </div>
        )}
        {paged.error && (
          <p className="alert" role="alert">
            続きを読み込めませんでした。画面を再読み込みしてください。
          </p>
        )}
      </div>

      {/*
        Mounted only while open, and keyed on the user: a dialog's form state
        initialises once, so a reused instance would open on the second person
        showing the first person's values.
      */}
      {dialog?.kind === 'create' && (
        <CreateUserDialog
          clinics={clinics.items}
          stores={stores.items}
          open
          onClose={() => setDialog(null)}
        />
      )}
    </Card>
  );
}
